import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, Database, Filter, Copy, Settings, Activity, Eye, TestTube, BarChart3, Clock, Shield, Wrench } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const CsvIngestionDebugPanel = () => {
  const [activeStep, setActiveStep] = useState(null);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [summary, setSummary] = useState([]);
  const [autoRun, setAutoRun] = useState(false);

  // Debug Steps Configuration - Following the 10-step playbook
  const debugSteps = [
    {
      id: 'step1',
      title: 'Verify Database Environment',
      description: 'Check if you\'re looking at the right database',
      icon: Database,
      severity: 'info',
      functionName: 'verify_database_environment'
    },
    {
      id: 'step2', 
      title: 'Count Raw vs Final Records',
      description: 'Compare raw upload counts with final database records',
      icon: BarChart3,
      severity: 'critical',
      functionName: 'count_raw_vs_final_records'
    },
    {
      id: 'step3',
      title: 'Check View Filters', 
      description: 'Analyze if you\'re looking at filtered views',
      icon: Filter,
      severity: 'warning',
      functionName: 'check_view_filters'
    },
    {
      id: 'step4',
      title: 'UI Query Filters',
      description: 'Check for time window, facility, or status filters',
      icon: Eye,
      severity: 'warning', 
      functionName: 'analyze_ui_filters'
    },
    {
      id: 'step5',
      title: 'Deduplication Analysis',
      description: 'Check if over-aggressive deduplication is collapsing records',
      icon: Copy,
      severity: 'critical',
      functionName: 'analyze_dedup_issues'
    },
    {
      id: 'step6',
      title: 'Constraint & Parse Failures',
      description: 'Analyze data validation and parsing errors',
      icon: AlertTriangle,
      severity: 'high',
      functionName: 'analyze_constraint_failures'
    },
    {
      id: 'step7',
      title: 'Job/Transaction Health',
      description: 'Check for stuck jobs and transaction rollbacks',
      icon: Activity,
      severity: 'high',
      functionName: 'check_job_transaction_health'
    },
    {
      id: 'step8',
      title: 'Permissions/RLS Check',
      description: 'Verify Row Level Security isn\'t hiding records',
      icon: Shield,
      severity: 'warning',
      functionName: 'check_permissions_rls'
    },
    {
      id: 'step9',
      title: 'Integration Sanity Test',
      description: 'Upload tiny CSV to verify end-to-end process',
      icon: TestTube,
      severity: 'info',
      functionName: 'create_integration_test_batch'
    },
    {
      id: 'step10',
      title: 'Comprehensive Summary',
      description: 'Complete diagnostic summary with recommendations',
      icon: Wrench,
      severity: 'info',
      functionName: 'get_comprehensive_debug_summary'
    }
  ];

  // Run a specific debug step
  const runDebugStep = async (step) => {
    setLoading(prev => ({ ...prev, [step?.id]: true }));
    
    try {
      const { data, error } = await supabase?.rpc(step?.functionName);

      if (error) {
        throw error;
      }

      setResults(prev => ({
        ...prev,
        [step?.id]: {
          success: true,
          data: data || [],
          timestamp: new Date()?.toISOString()
        }
      }));

    } catch (error) {
      console.error(`Error running ${step?.title}:`, error);
      setResults(prev => ({
        ...prev,
        [step?.id]: {
          success: false,
          error: error?.message,
          timestamp: new Date()?.toISOString()
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [step?.id]: false }));
    }
  };

  // Run all debug steps in sequence
  const runAllSteps = async () => {
    for (const step of debugSteps) {
      await runDebugStep(step);
      // Small delay between steps to prevent overwhelming the database
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Generate summary after all steps complete
    loadSummary();
  };

  // Load comprehensive summary
  const loadSummary = async () => {
    setLoading(prev => ({ ...prev, summary: true }));
    
    try {
      const { data, error } = await supabase?.rpc('get_comprehensive_debug_summary');

      if (error) throw error;
      
      setSummary(data || []);
    } catch (error) {
      console.error('Error loading summary:', error);
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  };

  // Clean up test data
  const cleanupTestData = async () => {
    try {
      const { data, error } = await supabase?.rpc('cleanup_integration_test_data');

      if (error) throw error;
      
      alert(`Cleanup completed: ${data}`);
    } catch (error) {
      console.error('Error cleaning up test data:', error);
      alert('Cleanup failed: ' + error?.message);
    }
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Get status color for results
  const getStatusColor = (level) => {
    switch (level) {
      case 'CRITICAL': return 'text-red-700 bg-red-100';
      case 'HIGH': return 'text-orange-700 bg-orange-100';
      case 'WARNING': return 'text-yellow-700 bg-yellow-100';
      case 'OK': return 'text-green-700 bg-green-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  // Format result data for display
  const formatResultData = (data, stepId) => {
    if (!data || !Array.isArray(data)) return null;

    return data?.map((item, index) => (
      <div key={index} className="bg-gray-50 rounded p-3 text-sm">
        {Object.entries(item)?.map(([key, value]) => (
          <div key={key} className="flex justify-between py-1">
            <span className="font-medium text-gray-600">
              {key?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}:
            </span>
            <span className="text-gray-900">
              {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : 
               typeof value === 'number' ? value?.toLocaleString() :
               value?.toString() || 'N/A'}
            </span>
          </div>
        ))}
      </div>
    ));
  };

  // Auto-run effect
  useEffect(() => {
    if (autoRun) {
      loadSummary();
    }
  }, [autoRun]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <Wrench className="w-7 h-7" />
              CSV Ingestion Debug Playbook
            </h2>
            <p className="text-red-100 mt-2">
              Systematic approach to identify why bulk CSV uploads report success but only show ~9 rows in database
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={runAllSteps}
              disabled={Object.values(loading)?.some(l => l)}
              className="bg-white text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${Object.values(loading)?.some(l => l) ? 'animate-spin' : ''}`} />
              Run All Steps
            </button>
            
            <button
              onClick={cleanupTestData}
              className="bg-red-600 border border-white text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2 font-medium"
            >
              <Settings className="w-4 h-4" />
              Cleanup Test Data
            </button>
          </div>
        </div>
      </div>
      {/* Summary Dashboard */}
      {summary?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Debug Summary Dashboard
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {summary?.map((item, index) => (
              <div key={index} className={`rounded-lg border p-4 ${getStatusColor(item?.status_level)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{item?.debug_category?.replace(/_/g, ' ')}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(item?.status_level)}`}>
                    {item?.status_level}
                  </span>
                </div>
                <p className="text-sm mb-2">{item?.finding_description}</p>
                {item?.percentage_impact > 0 && (
                  <p className="text-sm font-medium">Impact: {item?.percentage_impact}%</p>
                )}
                <p className="text-xs text-gray-600 mt-2">{item?.recommendation}</p>
              </div>
            ))}
          </div>
          
          {loading?.summary && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading summary...</span>
            </div>
          )}
        </div>
      )}
      {/* Debug Steps Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {debugSteps?.map((step) => {
          const Icon = step?.icon;
          const result = results?.[step?.id];
          const isLoading = loading?.[step?.id];
          
          return (
            <div key={step?.id} className={`bg-white rounded-lg border ${getSeverityColor(step?.severity)} p-6`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getSeverityColor(step?.severity)}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{step?.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{step?.description}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => runDebugStep(step)}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  {isLoading ? 'Running...' : 'Run Check'}
                </button>
              </div>
              {/* Results Display */}
              {result && (
                <div className="mt-4 border-t pt-4">
                  {result?.success ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Check completed successfully</span>
                        <span className="text-xs text-gray-500">
                          {new Date(result?.timestamp)?.toLocaleTimeString()}
                        </span>
                      </div>
                      
                      {result?.data?.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {formatResultData(result?.data, step?.id)}
                        </div>
                      ) : (
                        <div className="text-gray-500 text-sm italic">
                          No data returned - this may indicate the check passed or no issues were found
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-red-600">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Check failed</span>
                      </div>
                      <div className="text-red-700 text-sm bg-red-50 p-3 rounded">
                        {result?.error}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Footer Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Debugging Methodology</h4>
        <p className="text-blue-800 text-sm">
          This playbook implements a systematic 10-step approach to identify and resolve CSV ingestion discrepancies. 
          Each step builds on the previous to narrow down the root cause of why uploads claim "thousands" but database shows only a few records.
        </p>
        
        <div className="mt-3 flex items-center gap-4 text-sm text-blue-700">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Typical diagnosis time: 10-15 minutes
          </span>
          <span className="flex items-center gap-1">
            <Activity className="w-4 h-4" />
            Safe to run on production data
          </span>
        </div>
      </div>
    </div>
  );
};

export default CsvIngestionDebugPanel;